import React, { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'

import PageSectionHeader from '../PageSectionHeader'
import CTRGraph from './CTRGraph'
import SkeletonSummaryGraphs from './Skeleton'
import ImpressionsGraph from './ImpressionsGraph'
import ConversionRelatedGraphSections from './ConversionRelatedGraphSections'
import CPAGraph from './CPAGraph'
import GenderGraphSection from './GenderGraphSection'
import AgeGraphSection from './AgeGraphSection'
import PlatformGraphSection from './PlatformGraphSection'
import ClicksGraph from './ClicksGraph'
import PlacementGraphSection from './PlacementGraphSection'

import { useGraphData } from './useGraphData'

import { selectedPartnershipSelector } from '../../../modules/selectors/partnerships'

import useStyles from './styles'

function SummaryGraphs({
  getGraphs,
  clearGraphsAction,
  graphsSelector,
  graphsIsLoadingSelector,
  graphsWasLoadedSelector,
  graphsErrorSelector,
  hideGraphsSection,
  currencySymbol,
  scrollRef,
  RightPanel
}) {
  const classes = useStyles()
  const dispatch = useDispatch()

  const graphs = useSelector(graphsSelector)
  const graphsIsLoading = useSelector(graphsIsLoadingSelector)
  const graphsError = useSelector(graphsErrorSelector)
  const graphsWasLoaded = useSelector(graphsWasLoadedSelector)
  const selectedPartnership = useSelector(selectedPartnershipSelector)

  const { advanced_metrics: showAdvancedMetrics } = selectedPartnership

  const {
    data = [],
    gender_data: genderData = [],
    age_data: ageData = [],
    publisher_platform_data: platformData = [],
    platform_position_data: placementData = []
  } = graphs

  const graphData = useGraphData(data)
  const { impressionsData, ctrData, clicksData, cpaData } = graphData

  // sort data by percent descending
  // .sort method creates a new array that is a shallow copy of the original to avoid redux state mutation
  const sortedPlatformData = useMemo(
    () => platformData?.length && [...platformData].sort((a, b) => b.percent - a.percent),
    [platformData]
  )
  const sortedPlacementData = useMemo(
    () => placementData?.length && [...placementData].sort((a, b) => b.percent - a.percent),
    [placementData]
  )

  const isGraphsEmpty = graphsWasLoaded && !data.length
  const isNoImpressions = graphsWasLoaded && !impressionsData?.total

  const reusableProps = useMemo(
    () => ({
      currencySymbol,
      showAdvancedMetrics
    }),
    [currencySymbol, showAdvancedMetrics]
  )

  useEffect(() => {
    getGraphs()

    return () => {
      dispatch(clearGraphsAction())
    }
  }, [dispatch, getGraphs, clearGraphsAction])

  if (hideGraphsSection || graphsError || isGraphsEmpty || isNoImpressions) {
    return null
  }

  return (
    <section id="graphs-report">
      <PageSectionHeader title="Performance report" RightPanel={RightPanel} />
      {graphsIsLoading ? (
        <SkeletonSummaryGraphs />
      ) : (
        <div className={classes.graphsContainer} ref={scrollRef}>
          {impressionsData && <ImpressionsGraph rawGraphData={impressionsData} />}
          {clicksData && <ClicksGraph rawGraphData={clicksData} />}
          {ctrData && <CTRGraph rawGraphData={ctrData} />}
          {!!genderData?.length && <GenderGraphSection rawGraphData={genderData} {...reusableProps} />}
          {!!ageData?.length && <AgeGraphSection rawGraphData={ageData} {...reusableProps} />}
          {!!platformData?.length && <PlatformGraphSection rawGraphData={sortedPlatformData} {...reusableProps} />}
          {showAdvancedMetrics && (
            <>
              <ConversionRelatedGraphSections graphData={graphData} />
              {!!cpaData && <CPAGraph rawGraphData={cpaData} currencySymbol={currencySymbol} />}
              {!!placementData?.length && (
                <PlacementGraphSection rawGraphData={sortedPlacementData} {...reusableProps} />
              )}
            </>
          )}
        </div>
      )}
    </section>
  )
}

export default SummaryGraphs

SummaryGraphs.propTypes = {
  getGraphs: PropTypes.func.isRequired,
  clearGraphsAction: PropTypes.func.isRequired,
  graphsSelector: PropTypes.func.isRequired,
  graphsIsLoadingSelector: PropTypes.func.isRequired,
  graphsErrorSelector: PropTypes.func.isRequired,
  graphsWasLoadedSelector: PropTypes.func.isRequired,
  currencySymbol: PropTypes.string,
  hideGraphsSection: PropTypes.bool,
  RightPanel: PropTypes.object
}
